import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import * as Types from '@/types'
import { apiClient } from '@/services/api'

interface CertificatesState {
  jobCertificates: {
    items: Types.JobCertificate[]
    current: Types.JobCertificate | null
    total: number
    page: number
    pageSize: number
    filters: {
      operation_id?: string
      status?: string
    }
    isLoading: boolean
    error: string | null
  }
  approvalLogs: {
    data: Types.CertificateApprovalLog[]
    total: number
    isLoading: boolean
    error: string | null
  }
}

const initialState: CertificatesState = {
  jobCertificates: {
    items: [],
    current: null,
    total: 0,
    page: 1,
    pageSize: 20,
    filters: {},
    isLoading: false,
    error: null,
  },
  approvalLogs: {
    data: [],
    total: 0,
    isLoading: false,
    error: null,
  },
}

// Job Certificate async thunks
export const fetchJobCertificates = createAsyncThunk(
  'certificates/fetchJobCertificates',
  async (
    { page, limit, filters }: { page?: number; limit?: number; filters?: Record<string, any> },
    { rejectWithValue }
  ) => {
    try {
      const skip = page ? (page - 1) * (limit || 20) : 0
      const response = await apiClient.listJobCertificates({
        skip,
        limit,
        ...filters,
      })
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch job certificates')
    }
  }
)

export const fetchJobCertificate = createAsyncThunk(
  'certificates/fetchJobCertificate',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.getJobCertificate(id)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch job certificate')
    }
  }
)

export const createJobCertificate = createAsyncThunk(
  'certificates/createJobCertificate',
  async (request: Types.JobCertificateCreateRequest, { rejectWithValue }) => {
    try {
      const response = await apiClient.createJobCertificate(request)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create job certificate')
    }
  }
)

export const updateJobCertificate = createAsyncThunk(
  'certificates/updateJobCertificate',
  async (
    { id, updates }: { id: string; updates: Partial<Types.JobCertificate> },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.updateJobCertificate(id, updates)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update job certificate')
    }
  }
)

export const approveCertificate = createAsyncThunk(
  'certificates/approveCertificate',
  async (
    {
      id,
      action,
      comments,
      reviewed_by_id,
      signed_by_id,
    }: {
      id: string
      action: 'submit' | 'approve' | 'reject' | 'sign'
      comments?: string
      reviewed_by_id?: string
      signed_by_id?: string
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.approveCertificate(
        id,
        action,
        comments,
        reviewed_by_id,
        signed_by_id
      )
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to approve certificate')
    }
  }
)

export const fetchCertificateApprovalLog = createAsyncThunk(
  'certificates/fetchApprovalLog',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.getCertificateApprovalLog(id)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch approval log')
    }
  }
)

const certificatesSlice = createSlice({
  name: 'certificates',
  initialState,
  reducers: {
    clearError: (state) => {
      state.jobCertificates.error = null
      state.approvalLogs.error = null
    },
    setCertificateFilters: (state, action) => {
      state.jobCertificates.filters = action.payload
      state.jobCertificates.page = 1
    },
  },
  extraReducers: (builder) => {
    // Fetch Certificates
    builder
      .addCase(fetchJobCertificates.pending, (state) => {
        state.jobCertificates.isLoading = true
        state.jobCertificates.error = null
      })
      .addCase(fetchJobCertificates.fulfilled, (state, action) => {
        state.jobCertificates.isLoading = false
        state.jobCertificates.items = action.payload.items
        state.jobCertificates.total = action.payload.total
        state.jobCertificates.page = action.payload.page
        state.jobCertificates.pageSize = action.payload.page_size
      })
      .addCase(fetchJobCertificates.rejected, (state, action) => {
        state.jobCertificates.isLoading = false
        state.jobCertificates.error = action.payload as string
      })

    // Fetch Single Certificate
    builder
      .addCase(fetchJobCertificate.pending, (state) => {
        state.jobCertificates.isLoading = true
        state.jobCertificates.error = null
      })
      .addCase(fetchJobCertificate.fulfilled, (state, action) => {
        state.jobCertificates.isLoading = false
        state.jobCertificates.current = action.payload
      })
      .addCase(fetchJobCertificate.rejected, (state, action) => {
        state.jobCertificates.isLoading = false
        state.jobCertificates.error = action.payload as string
      })

    // Create Certificate
    builder
      .addCase(createJobCertificate.pending, (state) => {
        state.jobCertificates.isLoading = true
        state.jobCertificates.error = null
      })
      .addCase(createJobCertificate.fulfilled, (state, action) => {
        state.jobCertificates.isLoading = false
        state.jobCertificates.items.unshift(action.payload)
        state.jobCertificates.current = action.payload
      })
      .addCase(createJobCertificate.rejected, (state, action) => {
        state.jobCertificates.isLoading = false
        state.jobCertificates.error = action.payload as string
      })

    // Update Certificate
    builder
      .addCase(updateJobCertificate.pending, (state) => {
        state.jobCertificates.isLoading = true
        state.jobCertificates.error = null
      })
      .addCase(updateJobCertificate.fulfilled, (state, action) => {
        state.jobCertificates.isLoading = false
        const index = state.jobCertificates.items.findIndex((c) => c.id === action.payload.id)
        if (index !== -1) {
          state.jobCertificates.items[index] = action.payload
        }
        state.jobCertificates.current = action.payload
      })
      .addCase(updateJobCertificate.rejected, (state, action) => {
        state.jobCertificates.isLoading = false
        state.jobCertificates.error = action.payload as string
      })

    // Approve Certificate
    builder
      .addCase(approveCertificate.pending, (state) => {
        state.jobCertificates.isLoading = true
        state.jobCertificates.error = null
      })
      .addCase(approveCertificate.fulfilled, (state, action) => {
        state.jobCertificates.isLoading = false
        const index = state.jobCertificates.items.findIndex((c) => c.id === action.payload.id)
        if (index !== -1) {
          state.jobCertificates.items[index] = action.payload
        }
        state.jobCertificates.current = action.payload
      })
      .addCase(approveCertificate.rejected, (state, action) => {
        state.jobCertificates.isLoading = false
        state.jobCertificates.error = action.payload as string
      })

    // Fetch Approval Log
    builder
      .addCase(fetchCertificateApprovalLog.pending, (state) => {
        state.approvalLogs.isLoading = true
        state.approvalLogs.error = null
      })
      .addCase(fetchCertificateApprovalLog.fulfilled, (state, action) => {
        state.approvalLogs.isLoading = false
        state.approvalLogs.data = action.payload.items
        state.approvalLogs.total = action.payload.total
      })
      .addCase(fetchCertificateApprovalLog.rejected, (state, action) => {
        state.approvalLogs.isLoading = false
        state.approvalLogs.error = action.payload as string
      })
  },
})

export const { clearError, setCertificateFilters } = certificatesSlice.actions
export default certificatesSlice.reducer
